import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { client } from "../../helper/apiCall";
import ENDPOINTS from "../../constants/endpoints";

const initialState = {
    loading: false,
    students: [],
    selectedStudent: null,
    error: null
};

const appendIfPresent = (formData, key, value) => {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, String(value));
};

const buildStudentCreateFormData = (payload) => {
    const formData = new FormData();
    const {
        studentImageFile,
        branchId,
        placeId,
        fullName,
        email,
        birthdate,
        gender,
        phoneNumber,
        address,
        height,
        identityNumber,
        licenseStatus,
        licenseNumber,
        registrationDate,
        groupIds,
        ageGroupIds,
        packagePlanId,
        packagePrice,
        paidAmount,
        packageStartTime,
        packageEndTime,
        packageNote,
        parents,
    } = payload || {};

    appendIfPresent(formData, 'BranchId', branchId);
    appendIfPresent(formData, 'PlaceId', placeId);
    appendIfPresent(formData, 'FullName', fullName);
    appendIfPresent(formData, 'Email', email);
    if (birthdate instanceof Date) appendIfPresent(formData, 'Birthdate', birthdate.toISOString());
    else if (birthdate) appendIfPresent(formData, 'Birthdate', birthdate);
    appendIfPresent(formData, 'Gender', gender);
    appendIfPresent(formData, 'PhoneNumber', phoneNumber);
    appendIfPresent(formData, 'Address', address);
    if (height !== undefined && height !== null) formData.append('Height', String(height));
    appendIfPresent(formData, 'IdentityNumber', identityNumber);
    appendIfPresent(formData, 'LicenseStatus', licenseStatus);
    appendIfPresent(formData, 'LicenseNumber', licenseNumber);
    if (registrationDate) formData.append('RegistrationDate', (registrationDate instanceof Date ? registrationDate.toISOString() : String(registrationDate)));

    if (Array.isArray(groupIds)) groupIds.forEach((g, i) => { if (g !== undefined && g !== null) formData.append(`GroupIds[${i}]`, String(g)); });
    if (Array.isArray(ageGroupIds)) ageGroupIds.forEach((a, i) => { if (a !== undefined && a !== null) formData.append(`AgeGroupIds[${i}]`, String(a)); });

    if (packagePlanId !== undefined && packagePlanId !== null) formData.append('PackagePlanId', String(packagePlanId));
    if (packagePrice !== undefined && packagePrice !== null) formData.append('PackagePrice', String(packagePrice));
    if (paidAmount !== undefined && paidAmount !== null) formData.append('PaidAmount', String(paidAmount));
    if (packageStartTime) formData.append('PackageStartTime', (packageStartTime instanceof Date ? packageStartTime.toISOString() : String(packageStartTime)));
    if (packageEndTime) formData.append('PackageEndTime', (packageEndTime instanceof Date ? packageEndTime.toISOString() : String(packageEndTime)));
    appendIfPresent(formData, 'PackageNote', packageNote);

    if (Array.isArray(parents)) {
        parents.forEach((p, i) => {
            if (!p) return;
            appendIfPresent(formData, `Parents[${i}].FullName`, p.fullName);
            appendIfPresent(formData, `Parents[${i}].Email`, p.email);
            appendIfPresent(formData, `Parents[${i}].PhoneNumber`, p.phoneNumber);
            appendIfPresent(formData, `Parents[${i}].Address`, p.address);
            if (p.isEmergency !== undefined && p.isEmergency !== null) formData.append(`Parents[${i}].IsEmergency`, String(p.isEmergency));
            if (p.isSmsChecked !== undefined && p.isSmsChecked !== null) formData.append(`Parents[${i}].IsSmsChecked`, String(p.isSmsChecked));
        });
    }

    if (studentImageFile) formData.append('StudentImage', studentImageFile);

    return formData;
};

export const getStudents = createAsyncThunk(
    "getStudents",
    async (arg, thunkAPI) => {
        try {
            // Geriye dönük uyum: eskiden düz branchId geçiliyordu.
            const { branchId, placeId } = typeof arg === 'object' && arg !== null ? arg : { branchId: arg };
            const query = placeId ? `?branchId=${branchId}&placeId=${placeId}` : `?branchId=${branchId}`;
            const response = await client.get(`${ENDPOINTS.STUDENTS.GET_LIST}${query}`, {
                token: thunkAPI.getState().auth.token
            });
            return response.data.items || response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

export const deleteStudent = createAsyncThunk(
    "deleteStudent",
    async (id, thunkAPI) => {
        try {
            const response = await client.delete(ENDPOINTS.STUDENTS.DELETE.replace(':id', id), null, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

export const addStudent = createAsyncThunk(
    "addStudent",
    async (body, thunkAPI) => {
        try {
            const formData = buildStudentCreateFormData(body);
            const response = await client.post(ENDPOINTS.STUDENTS.CREATE, null, {
                token: thunkAPI.getState().auth.token,
                customBody: formData,
                headers: {}
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

export const updateStudent = createAsyncThunk(
    "updateStudent",
    async (body, thunkAPI) => {
        try {
            // FromBody JSON
            const response = await client.put(ENDPOINTS.STUDENTS.UPDATE, body, {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

export const updateStudentPhoto = createAsyncThunk(
    "updateStudentPhoto",
    async ({ id, studentImageFile }, thunkAPI) => {
        try {
            const formData = new FormData();
            if (studentImageFile) formData.append('Photo', studentImageFile);
            const url = ENDPOINTS.STUDENTS.UPDATE_PHOTO.replace(':id', id);
            const response = await client(url, {
                method: 'PUT',
                token: thunkAPI.getState().auth.token,
                customBody: formData,
                headers: {}
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

export const getStudentById = createAsyncThunk(
    "getStudentById",
    async (id, thunkAPI) => {
        try {
            const response = await client.get(ENDPOINTS.STUDENTS.GET_BY_ID.replace(':id', id), {
                token: thunkAPI.getState().auth.token
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    });

const studentsSlice = createSlice({
    name: "students",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSelectedStudent: (state) => {
            state.selectedStudent = null;
        },
        setSelectedStudent: (state, action) => {
            state.selectedStudent = action.payload;
        }
    },
    extraReducers(builder) {
        builder.addCase(getStudents.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getStudents.fulfilled, (state, action) => {
            state.loading = false;
            state.students = action.payload;
        });
        builder.addCase(getStudents.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        
        builder.addCase(deleteStudent.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteStudent.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(deleteStudent.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        
        builder.addCase(addStudent.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addStudent.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(addStudent.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        
        builder.addCase(getStudentById.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getStudentById.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedStudent = action.payload;
        });
        builder.addCase(getStudentById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        
        builder.addCase(updateStudent.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateStudent.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(updateStudent.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(updateStudentPhoto.pending, (state) => { state.loading = true; state.error = null; });
        builder.addCase(updateStudentPhoto.fulfilled, (state) => { state.loading = false; });
        builder.addCase(updateStudentPhoto.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    },
});

export const { clearError, clearSelectedStudent, setSelectedStudent } = studentsSlice.actions;
export default studentsSlice.reducer; 